import { Col, Row, Spinner, Button } from 'react-bootstrap';
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import TableRowAction from 'components/common/TableRowAction';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { useParams, useNavigate } from 'react-router-dom';
import paths from 'routes/paths';
import AddSchoolModal from './AddSchoolModal';
import EditSchoolModal from './EditSchoolModal';
import AddReportModal from './AddReportModal';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import { format } from 'date-fns';
import dayjs from 'dayjs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { supabase } from 'lib/supabase';
import { toast } from 'react-toastify';
import {
  useSchoolStudents,
  useCreateSchoolStudent,
  useUpdateSchoolStudent,
  useDeleteSchoolStudent,
  useSchoolReports,
  useCreateSchoolReport,
  useUpdateSchoolReport,
  useDeleteSchoolReport
} from 'hooks/useBeneficiaries';
import useUpload from 'hooks/useUpload';
import { useAuthContext } from 'context/authContext';
import { useBeneficiaryContext } from 'context/beneficiaryContext';
import CreateReminderModal from 'components/dashboards/CreateReminderModal';

// Component to handle document viewing with signed URLs
const ReportDocumentLink = ({ url, name }) => {
  const [loading, setLoading] = useState(false);

  const handleClick = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      // Extract file path from URL
      let filePath = '';
      if (url.includes('/storage/v1/object/public/documents/')) {
        filePath = url.split('/storage/v1/object/public/documents/')[1];
      } else if (url.includes('/storage/v1/object/sign/documents/')) {
        filePath = url
          .split('/storage/v1/object/sign/documents/')[1]
          .split('?')[0];
      } else {
        window.open(url, '_blank');
        setLoading(false);
        return;
      }

      const { data, error } = await supabase.storage
        .from('documents')
        .createSignedUrl(filePath, 3600);

      if (error) {
        console.error('Error creating signed URL:', error);
        toast.error('Failed to open document');
        setLoading(false);
        return;
      }

      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error opening document:', err);
      toast.error('Failed to open document');
    }
    setLoading(false);
  };

  return (
    <a
      href="#"
      onClick={handleClick}
      style={{ cursor: loading ? 'wait' : 'pointer' }}
    >
      {loading ? (
        <Spinner size="sm" animation="border" />
      ) : (
        <FontAwesomeIcon icon="link" />
      )}{' '}
      {name}
    </a>
  );
};

ReportDocumentLink.propTypes = {
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired
};

const School = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { beneficiary } = useBeneficiaryContext();
  const schoolColumns = [
    {
      accessor: 'school_name',
      Header: 'School Name',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { school } = rowData.row.original;
        return <>{school?.school_name || '-'}</>;
      }
    },
    {
      accessor: 'school_address',
      Header: 'Location',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { school } = rowData.row.original;
        return <>{school?.school_address || '-'}</>;
      }
    },
    {
      accessor: 'start_date',
      Header: 'Start Date',
      Cell: rowData => {
        const { start_date } = rowData.row.original;
        return start_date ? format(new Date(start_date), 'dd/MM/yyyy') : '-';
      }
    },
    {
      accessor: 'end_date',
      Header: 'End Date',
      Cell: rowData => {
        const { end_date } = rowData.row.original;
        return end_date ? format(new Date(end_date), 'dd/MM/yyyy') : 'Present';
      }
    },
    {
      accessor: 'user',
      Header: 'User',
      Cell: rowData => {
        const { user } = rowData.row.original;
        return <>{user ? user.first_name + ' ' + user.last_name : '-'}</>;
      }
    },
    {
      accessor: 'id_',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,

      Cell: rowData => {
        const { id } = rowData.row.original;
        return (
          <TableRowAction
            onCLickEdit={() =>
              setEditSchoolModal({ isOpen: true, schoolStudentId: id })
            }
            onCLickDelete={() => handleDeleteClick(id)}
            subject={permissionsKeyMap.facilities.children.school}
          />
        );
      }
    }
  ];

  const reportColumns = [
    {
      accessor: 'reporter',
      Header: 'Reporter',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { user } = rowData.row.original;
        return <>{user ? user.first_name + ' ' + user.last_name : '-'}</>;
      }
    },
    {
      accessor: 'report_date',
      Header: 'Date',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { report_date } = rowData.row.original;
        return report_date ? dayjs(report_date).format('DD/MM/YYYY') : '-';
      }
    },
    {
      accessor: 'comment',
      Header: 'Comment',
      cellProps: {
        className: 'py-2',
        style: { maxWidth: '200px' }
      },
      Cell: rowData => {
        const { comment } = rowData.row.original;
        return (
          <span
            style={{
              display: 'block',
              maxWidth: '200px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
            title={comment || ''}
          >
            {comment || '-'}
          </span>
        );
      }
    },
    {
      accessor: 'report_name',
      Header: 'Report File',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { report_name, report_document } = rowData.row.original;
        return report_document ? (
          <ReportDocumentLink
            url={report_document}
            name={report_name || 'Report'}
          />
        ) : (
          'No File'
        );
      }
    },
    {
      accessor: 'id_',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,
      Cell: rowData => {
        const { id } = rowData.row.original;
        return (
          <TableRowAction
            onCLickEdit={() => handleEditReportClick(rowData.row.original)}
            onCLickDelete={() => handleDeleteReportClick(id)}
            subject={permissionsKeyMap.sponsorship.children.beneficiaries}
          />
        );
      }
    }
  ];

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteReportModal, setShowDeleteReportModal] = useState(false);
  const [showEditReportModal, setShowEditReportModal] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [editObj, setEditObj] = useState(null);

  const [addSchoolModalShow, setAddSchoolModalShow] = useState(false);
  const [editSchoolModal, setEditSchoolModal] = useState({
    isOpen: false,
    schoolStudentId: null
  });
  const [addReportModalShow, setAddReportModalShow] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const { uploadDocument, loading: isUploadReportLoading } = useUpload();

  const { organization, selectedBranchId, user } = useAuthContext();

  // Use Supabase hooks for school students
  const {
    schoolStudents,
    isLoading: loading,
    refetch: refetchSchoolStudents
  } = useSchoolStudents(id);
  const { createSchoolStudent, isCreating: isAddSchoolStudentLoading } =
    useCreateSchoolStudent();
  const { updateSchoolStudent, isUpdating: isEditSchoolStudentLoading } =
    useUpdateSchoolStudent();
  const { deleteSchoolStudent } = useDeleteSchoolStudent();

  // Use Supabase hooks for school reports
  const {
    schoolReports,
    isLoading: reportsLoading,
    refetch: refetchSchoolReports
  } = useSchoolReports(id);
  const { createSchoolReport, isCreating: isAddSchoolReportLoading } =
    useCreateSchoolReport();
  const { updateSchoolReport, isUpdating: isUpdateSchoolReportLoading } =
    useUpdateSchoolReport();
  const { deleteSchoolReport } = useDeleteSchoolReport();

  const handleDeleteClick = id => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteReportClick = id => {
    setSelectedId(id);
    setShowDeleteReportModal(true);
  };

  const handleEditReportClick = obj => {
    // Navigate to edit page with report data
    navigate(paths.beneficiaryCreateSchoolReport.replace(':id', id), {
      state: { reportData: obj }
    });
  };

  const handleDeleteModalHide = () => {
    setSelectedId(null);
    setShowDeleteModal(false);
  };

  const handleDeleteRreportModalHide = () => {
    setSelectedId(null);
    setShowDeleteReportModal(false);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    await deleteSchoolStudent(selectedId);
    refetchSchoolStudents();
  };

  const handleConfirmDeleteReport = async () => {
    setShowDeleteReportModal(false);
    await deleteSchoolReport(selectedId);
    refetchSchoolReports();
  };

  const submitAddSchool = async values => {
    await createSchoolStudent({
      school_id: values.school,
      orphan_id: id,
      branch_id: selectedBranchId,
      organisation_id: organization.id,
      user_id: user.id,
      start_date: values.startDate === '' ? null : values.startDate,
      end_date: values.endDate === '' ? null : values.endDate
    });
    setAddSchoolModalShow(false);
    refetchSchoolStudents();
  };

  const submitEditSchool = async values => {
    await updateSchoolStudent({
      id: editSchoolModal.schoolStudentId,
      data: {
        school_id: values.school,
        start_date: values.startDate === '' ? null : values.startDate,
        end_date: values.endDate === '' ? null : values.endDate
      }
    });
    setEditSchoolModal({ isOpen: false, schoolStudentId: null });
    refetchSchoolStudents();
  };

  const submitAddReport = async values => {
    // reportDocument is now an array from RHFDocumentUpload
    const reportFile = values.reportDocument?.[0];
    let report_name;
    let report_document;

    if (reportFile && reportFile instanceof File) {
      report_name = reportFile.name.split('.')[0];
      try {
        report_document = await uploadDocument(reportFile, 'school-reports');
      } catch (err) {
        return;
      }
    }

    if (editObj) {
      await updateSchoolReport({
        id: editObj.id,
        data: {
          school_id: values.school,
          comment: values.comment,
          reporter: values.reporter,
          user_id: user.id,
          report_date: values.date,
          report_name: report_name || values.editReport?.name || undefined,
          report_document:
            report_document || values.editReport?.url || undefined
        }
      });
      setEditObj(null);
      setShowEditReportModal(false);
    } else {
      await createSchoolReport({
        branch_id: selectedBranchId,
        organisation_id: organization.id,
        orphan_id: id,
        school_id: values.school,
        user_id: user.id,
        reporter: values.reporter,
        comment: values.comment,
        report_date: values.date,
        report_name,
        report_document
      });
      setAddReportModalShow(false);
    }
    refetchSchoolReports();
  };

  const schoolOptions = useMemo(() => {
    return (
      schoolStudents?.map(item => {
        return { label: item.school?.school_name, value: item.school?.id };
      }) || []
    );
  }, [schoolStudents]);

  return (
    <>
      <ConfirmationModal
        show={showDeleteModal || showDeleteReportModal}
        onHide={
          showDeleteModal ? handleDeleteModalHide : handleDeleteRreportModalHide
        }
        title={showDeleteModal ? 'Remove School' : 'Delete Report'}
        handleConfirm={
          showDeleteModal ? handleConfirmDelete : handleConfirmDeleteReport
        }
        body={
          showDeleteModal
            ? 'Are you sure you want to remove selected School?'
            : 'Are you sure you want to remove selected Report?'
        }
      />

      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={schoolStudents || []}
            columns={schoolColumns}
            title="Schools"
            addButtonLabel="Add"
            onClickAddNew={() => {
              setAddSchoolModalShow(true);
            }}
            loading={loading}
            addNew
            subject={permissionsKeyMap.facilities.children.school}
          />
        </Col>
      </Row>

      <Row className="mt-3">
        <Col md={12} xxl={12} className="h-md-100">
          <CustomAdvanceTable
            data={schoolReports || []}
            columns={reportColumns}
            title="Reports"
            addButtonLabel="Add"
            onClickAddNew={() => {
              setAddReportModalShow(true);
              setEditObj(null);
            }}
            loading={reportsLoading}
            addNew
            subject={permissionsKeyMap.facilities.children.school}
            headerContent={
              <>
                <Button
                  variant="falcon-default"
                  size="sm"
                  className="me-2"
                  onClick={() => setShowReminderModal(true)}
                >
                  <FontAwesomeIcon icon="bell" className="me-1" />
                  Set Reminder
                </Button>
                <Button
                  variant="falcon-primary"
                  size="sm"
                  className="me-2"
                  onClick={() =>
                    navigate(paths.beneficiaryCreateSchoolReport.replace(':id', id))
                  }
                >
                  <FontAwesomeIcon icon="file-pdf" className="me-1" />
                  Create Report
                </Button>
              </>
            }
          />
        </Col>
      </Row>
      {addSchoolModalShow && (
        <AddSchoolModal
          show={addSchoolModalShow}
          onSubmit={submitAddSchool}
          isSubmitLoading={isAddSchoolStudentLoading}
          onHide={() => {
            setAddSchoolModalShow(false);
          }}
          title={'Add School'}
        />
      )}
      {editSchoolModal.isOpen && (
        <EditSchoolModal
          show={editSchoolModal.isOpen}
          id={editSchoolModal.schoolStudentId}
          onSubmit={submitEditSchool}
          isSubmitLoading={isEditSchoolStudentLoading}
          onHide={() =>
            setEditSchoolModal({ isOpen: false, schoolStudentId: null })
          }
          title="Edit School"
        />
      )}
      {(addReportModalShow || showEditReportModal) && (
        <AddReportModal
          show={addReportModalShow || showEditReportModal}
          onHide={() => {
            setAddReportModalShow(false);
            setShowEditReportModal(false);
          }}
          title={addReportModalShow ? 'Add Report' : 'Edit Report'}
          loading={
            isAddSchoolReportLoading ||
            isUpdateSchoolReportLoading ||
            isUploadReportLoading
          }
          onSubmit={submitAddReport}
          schoolOptions={schoolOptions}
          initValues={editObj}
        />
      )}

      <CreateReminderModal
        show={showReminderModal}
        onHide={() => setShowReminderModal(false)}
        defaultValues={{
          title: `School report due for ${beneficiary?.first_name || ''} ${beneficiary?.last_name || ''}`.trim(),
          description: `School report is due for ${beneficiary?.first_name || ''} ${beneficiary?.last_name || ''}${beneficiary?.simple_id ? ` (B${beneficiary.simple_id})` : ''}`,
          reminder_type: 'update'
        }}
      />
    </>
  );
};

export default School;
